/**
 * Admin Dashboard
 * Section 6.1.1 - Overview with stats
 */

import { useState, useEffect } from 'react';

// Import database directly for stats (client-side)
import oilSpecsData from '../../database/oil_specs.json';

function Dashboard() {
  const [stats, setStats] = useState({
    totalMakes: 0,
    totalModels: 0,
    totalEngines: 0,
    byCategory: {
      standard: 0,
      european: 0,
      truck: 0
    },
    recentErrors: 0,
    validationStatus: 'loading'
  });

  useEffect(() => {
    calculateStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const calculateStats = () => {
    // Calculate stats from local data
    const makes = new Set(oilSpecsData.map(s => s.make));
    const models = new Set(oilSpecsData.map(s => `${s.make}_${s.model}`));

    const byCategory = {
      standard: oilSpecsData.filter(s => s.brand_category === 'standard').length,
      european: oilSpecsData.filter(s => s.brand_category === 'european').length,
      truck: oilSpecsData.filter(s => s.brand_category === 'truck').length
    };

    // Run quick validation
    const validationIssues = runQuickValidation();

    setStats({
      totalMakes: makes.size,
      totalModels: models.size,
      totalEngines: oilSpecsData.length,
      byCategory,
      recentErrors: 0, // Would come from API
      validationStatus: validationIssues.length === 0 ? 'passed' : 'issues',
      validationIssues
    });
  };

  const runQuickValidation = () => {
    const issues = [];

    // Check for missing required fields
    oilSpecsData.forEach((spec, index) => {
      if (!spec.oil_type) {
        issues.push({ type: 'missing', field: 'oil_type', record: index });
      }
      if (!spec.oil_capacity_qts) {
        issues.push({ type: 'missing', field: 'oil_capacity_qts', record: index });
      }
      if (!spec.brand_category) {
        issues.push({ type: 'missing', field: 'brand_category', record: index });
      }
    });

    // Check for out-of-range values
    oilSpecsData.forEach((spec, index) => {
      if (spec.oil_capacity_qts && (spec.oil_capacity_qts < 2.5 || spec.oil_capacity_qts > 16)) {
        issues.push({ type: 'range', field: 'oil_capacity_qts', record: index, value: spec.oil_capacity_qts });
      }
      if (spec.drain_plug_torque_ft_lbs && (spec.drain_plug_torque_ft_lbs < 10 || spec.drain_plug_torque_ft_lbs > 40)) {
        issues.push({ type: 'range', field: 'drain_plug_torque_ft_lbs', record: index, value: spec.drain_plug_torque_ft_lbs });
      }
    });

    return issues.slice(0, 10); // Limit to first 10
  };

  return (
    <div className="dashboard">
      <div className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Overview of your vehicle database and system status</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card standard">
          <div className="stat-value">{stats.totalMakes}</div>
          <div className="stat-label">Makes</div>
        </div>
        <div className="stat-card standard">
          <div className="stat-value">{stats.totalModels}</div>
          <div className="stat-label">Models</div>
        </div>
        <div className="stat-card standard">
          <div className="stat-value">{stats.totalEngines.toLocaleString()}</div>
          <div className="stat-label">Engine Variants</div>
        </div>
        <div className={`stat-card ${stats.recentErrors > 0 ? 'error' : 'success'}`}>
          <div className="stat-value">{stats.recentErrors}</div>
          <div className="stat-label">Errors (24h)</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="admin-card">
        <h2>Records by Category</h2>
        <div className="category-bars">
          <div className="category-bar">
            <div className="category-info">
              <span className="category-name">Standard</span>
              <span className="category-count">{stats.byCategory.standard}</span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill standard"
                style={{ width: `${(stats.byCategory.standard / stats.totalEngines * 100)}%` }}
              />
            </div>
          </div>
          <div className="category-bar">
            <div className="category-info">
              <span className="category-name">European</span>
              <span className="category-count">{stats.byCategory.european}</span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill european"
                style={{ width: `${(stats.byCategory.european / stats.totalEngines * 100)}%` }}
              />
            </div>
          </div>
          <div className="category-bar">
            <div className="category-info">
              <span className="category-name">Truck/Diesel</span>
              <span className="category-count">{stats.byCategory.truck}</span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill truck"
                style={{ width: `${(stats.byCategory.truck / stats.totalEngines * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Validation Status */}
      <div className="admin-card">
        <h2>Validation Status</h2>
        {stats.validationStatus === 'loading' ? (
          <p>Running validation checks...</p>
        ) : stats.validationStatus === 'passed' ? (
          <div className="validation-passed">
            <span className="check-icon">✓</span>
            All validation checks passed
          </div>
        ) : (
          <div className="validation-issues">
            <div className="issues-header">
              <span className="warning-icon">⚠</span>
              {stats.validationIssues?.length || 0} issues found
            </div>
            <ul className="issues-list">
              {stats.validationIssues?.map((issue, i) => (
                <li key={i}>
                  {issue.type === 'missing'
                    ? `Missing ${issue.field} at record #${issue.record}`
                    : `${issue.field} out of range (${issue.value}) at record #${issue.record}`
                  }
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="admin-card">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <a href="/admin/vehicles" className="admin-btn admin-btn-primary">
            View Vehicle Database
          </a>
          <a href="/admin/pricing" className="admin-btn admin-btn-secondary">
            Edit Pricing Config
          </a>
          <a href="/admin/import-export" className="admin-btn admin-btn-secondary">
            Import/Export Data
          </a>
        </div>
      </div>

      <style>{`
        .category-bars {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .category-bar {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .category-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
        }

        .category-name {
          font-weight: 500;
          color: #374151;
        }

        .category-count {
          color: #6b7280;
        }

        .bar-track {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .bar-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .bar-fill.standard { background: #3b82f6; }
        .bar-fill.european { background: #f59e0b; }
        .bar-fill.truck { background: #ef4444; }

        .validation-passed {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #059669;
          font-weight: 500;
        }

        .check-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: #d1fae5;
          border-radius: 50%;
          font-size: 0.875rem;
        }

        .validation-issues {
          color: #92400e;
        }

        .issues-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          margin-bottom: 0.75rem;
        }

        .warning-icon {
          font-size: 1.25rem;
        }

        .issues-list {
          margin: 0;
          padding-left: 1.5rem;
          font-size: 0.875rem;
          color: #78350f;
        }

        .issues-list li {
          margin-bottom: 0.25rem;
        }

        .quick-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
